/**
 * Curated web media shortlist (review set).
 * Sources live in /images; web exports in /public/images/web.
 * Logo variants in /public/images/brand (white-on-transparent / navy).
 */
export const BRAND = {
  /** Interim monoline ecoeng.net wordmark — white on transparent (navy UI) */
  logo: "/images/brand/ecoeng-logo-header@2x.png",
  logoWhite: "/images/brand/ecoeng-logo-white.png",
  logoWhite2x: "/images/brand/ecoeng-logo-white@2x.png",
  logoWhite4x: "/images/brand/ecoeng-logo-white@4x.png",
  logoDark: "/images/brand/ecoeng-logo-dark.png",
  logoOnNavy: "/images/brand/ecoeng-logo-on-navy.png",
  logoHeader: "/images/brand/ecoeng-logo-header.png",
  logoHeader2x: "/images/brand/ecoeng-logo-header@2x.png",
  logoHeader4x: "/images/brand/ecoeng-logo-header@4x.png",
  favicon: "/images/brand/ecoeng-favicon.png",
  /** Source archive */
  logoSource: "/images/brand/ecoeng-logo-interim-source.png",
  alt: "EcoEng.net — Ecological Engineering network",
} as const;

export type SiteImageId =
  | "crew-clearing"
  | "infra-chamber"
  | "rural-windmill"
  | "field-ecology"
  | "materials-glass"
  | "architecture-brutalist"
  | "heritage-jacaranda"
  | "biomimicry-mural"
  | "coastal-marine"
  | "insights-carbon";

export interface SiteImage {
  id: SiteImageId;
  src: string;
  alt: string;
  caption: string;
  uses: string[];
  audiences?: string[];
}

export const SITE_IMAGES: Record<SiteImageId, SiteImage> = {
  "crew-clearing": {
    id: "crew-clearing",
    src: "/images/web/crew-clearing.jpg",
    alt: "Field crew clearing vegetation on a project site",
    caption: "Construction crews need controls that hold up on site",
    uses: ["contractors", "home-gallery", "services"],
    audiences: ["contractors"],
  },
  "infra-chamber": {
    id: "infra-chamber",
    src: "/images/web/infra-chamber.jpg",
    alt: "Circular infrastructure chamber in a vegetated corridor",
    caption: "Infrastructure in living landscapes — where pathways get complex",
    uses: ["local-gov", "energy", "home-hero-secondary", "home-gallery"],
    audiences: ["local-gov", "energy"],
  },
  "rural-windmill": {
    id: "rural-windmill",
    src: "/images/web/rural-windmill.jpg",
    alt: "Rural landscape with windmill under eucalypts",
    caption: "Regional sites where programme risk is environmental",
    uses: ["developers", "home-hero", "home-gallery"],
    audiences: ["developers", "mining"],
  },
  "field-ecology": {
    id: "field-ecology",
    src: "/images/web/field-ecology.jpg",
    alt: "Tagged timber and riparian vegetation on a field site",
    caption: "Field evidence that stands up in review",
    uses: ["engineers", "track-record", "home-gallery"],
    audiences: ["engineers"],
  },
  "materials-glass": {
    id: "materials-glass",
    src: "/images/web/materials-glass.jpg",
    alt: "Drum of crushed green glass for materials reuse",
    caption: "Materials and soils decisions with a clear framework",
    uses: ["soils", "home-gallery", "services"],
    audiences: ["engineers", "mining"],
  },
  "architecture-brutalist": {
    id: "architecture-brutalist",
    src: "/images/web/architecture-brutalist.jpg",
    alt: "Brutalist concrete building in an urban streetscape",
    caption: "Urban form — design constrained by environmental reality",
    uses: ["architects", "home-gallery"],
    audiences: ["architects"],
  },
  "heritage-jacaranda": {
    id: "heritage-jacaranda",
    src: "/images/web/heritage-jacaranda.jpg",
    alt: "Jacaranda beside a heritage cottage roof",
    caption: "Place and heritage settings that shape pathways",
    uses: ["architects", "home-gallery"],
    audiences: ["architects"],
  },
  "biomimicry-mural": {
    id: "biomimicry-mural",
    src: "/images/web/biomimicry-mural.jpg",
    alt: "Biomimetic wall mural of waterways and coral forms",
    caption: "Systems thinking — without the soft eco theatre",
    uses: ["insights", "home-split", "home-gallery"],
  },
  "coastal-marine": {
    id: "coastal-marine",
    src: "/images/web/coastal-marine.jpg",
    alt: "Clear coastal water and breaking wave",
    caption: "Coastal and marine interfaces that block approvals",
    uses: ["blue-carbon", "marine", "energy", "home-gallery"],
    audiences: ["energy"],
  },
  "insights-carbon": {
    id: "insights-carbon",
    src: "/images/web/insights-carbon.jpg",
    alt: "Carbon and regeneration references on a project table",
    caption: "Carbon and regeneration — framed before deep spend",
    uses: ["insights", "home-gallery"],
  },
};

/** Map audience id → primary photo */
export const AUDIENCE_IMAGE: Record<string, SiteImageId> = {
  architects: "architecture-brutalist",
  engineers: "field-ecology",
  developers: "rural-windmill",
  "local-gov": "infra-chamber",
  mining: "materials-glass",
  energy: "coastal-marine",
  contractors: "crew-clearing",
};

export const GALLERY_ORDER: SiteImageId[] = [
  "rural-windmill",
  "crew-clearing",
  "infra-chamber",
  "field-ecology",
  "materials-glass",
  "architecture-brutalist",
  "heritage-jacaranda",
  "coastal-marine",
  "biomimicry-mural",
  "insights-carbon",
];

export function img(id: SiteImageId): SiteImage {
  return SITE_IMAGES[id];
}
